import React, { useState, useEffect, useRef } from 'react';
import { AnimatedSection } from './AnimatedSection';

export const BringYourIdeatoLife = ({ bringyoreideatolife }) => {
  const data = bringyoreideatolife;

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    ideaTitle: '',
    description: '',
    link: '',
    dataPrivacy: false,
  });
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // Reference for scrolling
  const formRef = useRef(null);

  // Smooth scroll when form opens
  useEffect(() => {
    if (showForm && formRef.current) {
      setTimeout(() => {
        formRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  }, [showForm]);

  // Handle Text Inputs
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle File Input
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  // Toggle Form Visibility
  const toggleForm = () => {
    setStatus('');
    setShowForm(!showForm);
  };

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');

    const payload = new FormData();
    payload.append('projectName', formData.ideaTitle);
    payload.append('positions', JSON.stringify(['Idea Submission']));
    payload.append('message', formData.description);
    payload.append('name', formData.name);
    payload.append('surname', '-');
    payload.append('number', '-');
    payload.append('email', formData.email);
    payload.append('dataPrivacy', formData.dataPrivacy ? 1 : 0);
    payload.append('proudProject', formData.link);
    payload.append(
      'additionalMessage',
      'Submitted via Bring Your Idea to Life Page'
    );

    if (file) {
      payload.append('resume', file);
    } else {
      payload.append('resume', '');
    }
    payload.append('coverLetter', '');

    try {
      const response = await fetch('https://mahanc.com/apply.php', {
        method: 'POST',
        body: payload,
      });
      const result = await response.json();

      if (response.ok || response.status === 201) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          ideaTitle: '',
          description: '',
          link: '',
          dataPrivacy: false,
        });
        setFile(null);
      } else {
        setStatus(`error: ${result.message}`);
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="bring-idea" className="bi-section">
      <div className="container">
        {/* === HERO INTRO === */}
        <div className="bi-hero text-center">
          <AnimatedSection>
            <h2 className="bi-title">{data.title}</h2>
            <div className="bi-divider"></div>
            <p className="bi-intro">{data.intro}</p>

            <div className="bi-cta-wrapper">
              <button
                className={`bi-btn-main ${showForm ? 'active' : ''}`}
                onClick={toggleForm}
              >
                {showForm ? 'Close Application' : 'Apply to Build With Us'}
              </button>
            </div>
          </AnimatedSection>
        </div>

        {/* === STRATEGIC FIT CARDS === */}
        {data.strategicFit && (
          <AnimatedSection>
            <div className="bi-strategy-grid">
              {data.strategicFit.map((item, index) => (
                <div className="bi-card" key={index}>
                  <div className="bi-card-icon">
                    <i className="fa fa-lightbulb-o"></i>
                  </div>
                  <h4 className="bi-card-q">{item.question}</h4>
                  <p className="bi-card-a">{item.answer}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        )}

        {/* === PROCESS STEPS === */}
        {data.steps && data.steps.length > 0 && (
          <div className="bi-process">
            <h3 className="bi-section-header">How It Works</h3>
            <div className="bi-steps-container">
              {data.steps.map((step, index) => (
                <div
                  className={`bi-step-row ${index % 2 !== 0 ? 'reverse' : ''}`}
                  key={index}
                >
                  <div className="bi-step-content">
                    <span className="bi-step-number">0{index + 1}</span>
                    <h3>{step.title}</h3>
                    <div
                      className="bi-step-text"
                      dangerouslySetInnerHTML={{ __html: step.text }}
                    />
                  </div>
                  <div className="bi-step-image">
                    <img src={step.image} alt={step.title} loading="lazy" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* === FORM SECTION === */}
        {showForm && (
          <div ref={formRef} className="bi-form-wrapper fade-in-up">
            <div className="bi-form-card">
              <div className="bi-form-header">
                <h3>Submit Your Vision</h3>
                <p>Confidential • Secure • Professional</p>
              </div>

              <form className="bi-form" onSubmit={handleSubmit}>
                <div className="bi-form-row">
                  <div className="bi-input-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="e.g. John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="bi-input-group">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="bi-input-group">
                  <label>Project Title *</label>
                  <input
                    type="text"
                    name="ideaTitle"
                    placeholder="e.g. Next Gen FinTech App"
                    value={formData.ideaTitle}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="bi-input-group">
                  <label>The Concept *</label>
                  <textarea
                    name="description"
                    rows="5"
                    placeholder="Describe your idea, target audience, and goals..."
                    value={formData.description}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="bi-input-group">
                  <label>Link (Optional)</label>
                  <input
                    type="url"
                    name="link"
                    placeholder="https://..."
                    value={formData.link}
                    onChange={handleChange}
                  />
                </div>

                {/* --- MODERN FILE UPLOAD --- */}
                <div className="bi-input-group file-group">
                  <label>Upload Pitch Deck (PDF)</label>
                  <div className={`bi-file-dropzone ${file ? 'has-file' : ''}`}>
                    {/* The actual input is hidden but covers the div to capture clicks */}
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileChange}
                      className="hidden-file-input"
                    />

                    <div className="dropzone-content">
                      {file ? (
                        // STATE: File Selected
                        <div className="file-success">
                          <i className="fa fa-file-pdf-o file-icon"></i>
                          <div className="file-info">
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                          <span className="change-file-link">Change File</span>
                        </div>
                      ) : (
                        // STATE: No File Selected
                        <div className="file-prompt">
                          <div className="icon-wrapper">
                            <i className="fa fa-cloud-upload"></i>
                          </div>
                          <p className="primary-text">
                            Click to upload or drag & drop
                          </p>
                          <p className="secondary-text">PDF only (Max 10MB)</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* CHECKBOX */}
                <div className="bi-checkbox-group">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      name="dataPrivacy"
                      checked={formData.dataPrivacy}
                      onChange={handleChange}
                      required
                    />
                    <span className="checkmark"></span>
                    <span className="label-text">
                      I agree to the Data Privacy Policy and consent to
                      processing my data.
                    </span>
                  </label>
                </div>

                <div className="bi-form-actions">
                  <button
                    type="submit"
                    className="bi-btn-submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <span>
                        <i className="fa fa-spinner fa-spin"></i> Sending...
                      </span>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </div>

                {status === 'success' && (
                  <div className="bi-status success">
                    <i className="fa fa-check-circle"></i> Application sent
                    successfully! We will contact you soon.
                  </div>
                )}
                {status && status.startsWith('error') && (
                  <div className="bi-status error">
                    <i className="fa fa-exclamation-circle"></i> Submission
                    failed. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>
        )}

        {/* === BOTTOM CTA (Visible when form is hidden) === */}
        {!showForm && (
          <div className="bi-bottom-cta">
            <p>Ready to start building?</p>
            <button className="bi-btn-outline" onClick={toggleForm}>
              Submit Your Idea
            </button>
          </div>
        )}
      </div>

      {/* ==============================================
          CSS STYLES
      ============================================== */}
      <style jsx>{`
        /* --- SECTION & HERO --- */
        .bi-section {
          padding-top: 140px;
          padding-bottom: 80px;
          background-color: #ffffff;
          overflow: hidden;
        }

        .bi-title {
          font-family: 'Raleway', sans-serif;
          font-weight: 800;
          color: #333;
          margin-bottom: 20px;
          position: relative;
          display: inline-block;
        }

        .bi-divider {
          width: 70px;
          height: 3px;
          background: linear-gradient(to right, #5ca9fb 0%, #6372ff 100%);
          margin: 0 auto 30px auto;
          border-radius: 2px;
        }

        .bi-intro {
          max-width: 800px;
          margin: 0 auto 40px;
          font-size: 18px;
          line-height: 1.6;
          color: #666;
        }

        .bi-cta-wrapper {
          margin-bottom: 60px;
        }

        /* --- BUTTONS --- */
        .bi-btn-main {
          background: linear-gradient(to right, #6372ff 0%, #5ca9fb 100%);
          border: none;
          border-radius: 50px;
          color: #fff;
          padding: 15px 40px;
          font-size: 16px;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          cursor: pointer;
          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease;
          box-shadow: 0 4px 15px rgba(99, 114, 255, 0.3);
        }

        .bi-btn-main:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(99, 114, 255, 0.4);
        }

        .bi-btn-main.active {
          background: #333;
          box-shadow: none;
        }

        .bi-btn-outline {
          background: transparent;
          border: 2px solid #6372ff;
          color: #6372ff;
          padding: 12px 30px;
          font-weight: 600;
          border-radius: 50px;
          transition: all 0.3s;
          cursor: pointer;
          text-transform: uppercase;
        }

        .bi-btn-outline:hover {
          background: #6372ff;
          color: #fff;
        }

        .bi-bottom-cta {
          text-align: center;
          margin-top: 80px;
        }
        .bi-bottom-cta p {
          font-size: 18px;
          color: #777;
          margin-bottom: 15px;
        }

        /* --- STRATEGY GRID --- */
        .bi-strategy-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
          margin-bottom: 80px;
        }

        .bi-card {
          background: #fff;
          padding: 40px 30px;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          transition: transform 0.3s ease;
          border-top: 4px solid transparent;
          text-align: center;
        }

        .bi-card:hover {
          transform: translateY(-10px);
          border-top: 4px solid #6372ff;
        }

        .bi-card-icon {
          font-size: 30px;
          color: #5ca9fb;
          margin-bottom: 20px;
        }

        .bi-card-q {
          font-size: 18px;
          font-weight: 700;
          color: #333;
          margin-bottom: 15px;
        }

        .bi-card-a {
          font-size: 15px;
          color: #666;
          line-height: 1.6;
        }

        /* --- PROCESS STEPS --- */
        .bi-process {
          margin-bottom: 60px;
        }

        .bi-section-header {
          text-align: center;
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 50px;
          color: #333;
        }

        .bi-steps-container {
          display: flex;
          flex-direction: column;
          gap: 60px;
        }

        .bi-step-row {
          display: flex;
          align-items: center;
          gap: 50px;
        }

        .bi-step-row.reverse {
          flex-direction: row-reverse;
        }

        .bi-step-content {
          flex: 1;
        }

        .bi-step-number {
          display: block;
          font-size: 60px;
          font-weight: 800;
          color: rgba(99, 114, 255, 0.1);
          line-height: 1;
          margin-bottom: -20px;
          margin-left: -5px;
        }

        .bi-step-content h3 {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 15px;
          color: #333;
        }

        .bi-step-text {
          font-size: 16px;
          color: #666;
          line-height: 1.7;
        }

        /* --- IMAGE FIX --- */
        .bi-step-image {
          flex: 1;
          max-width: 420px; /* Limits the size of the image container */
          width: 100%; /* Ensures it takes available space up to max-width */
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
          transform: rotate(2deg);
          transition: transform 0.3s;
        }

        .bi-step-row.reverse .bi-step-image {
          transform: rotate(-2deg);
        }

        .bi-step-image:hover {
          transform: rotate(0);
        }

        .bi-step-image img {
          width: 100%;
          height: auto;
          display: block;
          object-fit: cover;
        }

        /* --- FORM STYLING --- */
        .bi-form-wrapper {
          margin-top: 40px;
          padding: 10px;
          display: flex;
          justify-content: center;
        }

        .bi-form-card {
          width: 100%;
          max-width: 800px;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          padding: 40px 50px;
          border: 1px solid #f0f0f0;
          position: relative;
        }

        .bi-form-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 6px;
          background: linear-gradient(to right, #5ca9fb, #6372ff);
          border-radius: 20px 20px 0 0;
        }

        .bi-form-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .bi-form-header h3 {
          font-size: 26px;
          margin-bottom: 5px;
          color: #333;
        }

        .bi-form-header p {
          font-size: 14px;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .bi-form-row {
          display: flex;
          gap: 20px;
        }

        .bi-input-group {
          margin-bottom: 25px;
          flex: 1;
          text-align: left;
        }

        .bi-input-group label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 8px;
          color: #444;
        }

        .bi-input-group input[type='text'],
        .bi-input-group input[type='email'],
        .bi-input-group input[type='url'],
        .bi-input-group textarea {
          width: 100%;
          padding: 12px 15px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 15px;
          font-family: 'Open Sans', sans-serif;
          transition: all 0.3s;
          background: #f8fafc;
          color: #334155;
        }

        .bi-input-group input:focus,
        .bi-input-group textarea:focus {
          border-color: #6372ff;
          background: #fff;
          outline: none;
          box-shadow: 0 0 0 3px rgba(99, 114, 255, 0.1);
        }

        /* --- MODERN FILE UPLOAD --- */
        .bi-file-dropzone {
          position: relative;
          width: 100%;
          border: 2px dashed #cbd5e1;
          border-radius: 12px;
          background-color: #f8fafc;
          transition: all 0.3s ease;
          overflow: hidden;
          cursor: pointer;
          text-align: center;
        }

        .bi-file-dropzone:hover {
          border-color: #6372ff;
          background-color: #f0f4ff;
        }

        .bi-file-dropzone.has-file {
          border-style: solid;
          border-color: #28a745;
          background-color: #f0fff4;
        }

        .hidden-file-input {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
          z-index: 10;
        }

        .dropzone-content {
          padding: 30px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        }

        .icon-wrapper {
          width: 50px;
          height: 50px;
          background: #e0e7ff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          color: #6372ff;
          font-size: 24px;
        }

        .bi-file-dropzone:hover .icon-wrapper {
          background: #6372ff;
          color: #fff;
          transform: scale(1.1);
          transition: all 0.3s ease;
        }

        .primary-text {
          font-size: 16px;
          font-weight: 600;
          color: #334155;
          margin: 0 0 5px 0;
        }

        .secondary-text {
          font-size: 13px;
          color: #94a3b8;
          margin: 0;
        }

        .file-success {
          display: flex;
          align-items: center;
          gap: 15px;
          width: 100%;
          max-width: 90%;
        }

        .file-icon {
          font-size: 32px;
          color: #ef4444;
        }

        .file-info {
          flex: 1;
          text-align: left;
          overflow: hidden;
        }

        .file-name {
          display: block;
          font-size: 15px;
          font-weight: 600;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .file-size {
          display: block;
          font-size: 12px;
          color: #666;
          margin-top: 2px;
        }

        .change-file-link {
          font-size: 13px;
          font-weight: 600;
          color: #6372ff;
          text-decoration: underline;
        }

        /* --- CHECKBOX --- */
        .bi-checkbox-group {
          margin-bottom: 30px;
          text-align: left;
        }

        .checkbox-container {
          display: flex;
          align-items: center;
          cursor: pointer;
          font-size: 14px;
          user-select: none;
        }

        .checkbox-container input {
          position: absolute;
          opacity: 0;
          cursor: pointer;
          height: 0;
          width: 0;
        }

        .checkmark {
          height: 20px;
          width: 20px;
          background-color: #eee;
          border-radius: 4px;
          margin-right: 12px;
          position: relative;
          flex-shrink: 0;
          transition: background-color 0.2s;
        }

        .checkbox-container:hover input ~ .checkmark {
          background-color: #ccc;
        }

        .checkbox-container input:checked ~ .checkmark {
          background-color: #6372ff;
        }

        .checkmark:after {
          content: '';
          position: absolute;
          display: none;
        }

        .checkbox-container input:checked ~ .checkmark:after {
          display: block;
        }

        .checkbox-container .checkmark:after {
          left: 7px;
          top: 3px;
          width: 6px;
          height: 12px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }

        .label-text {
          color: #555;
          line-height: 1.4;
        }

        /* --- SUBMIT ACTIONS --- */
        .bi-btn-submit {
          width: 100%;
          padding: 15px;
          background: linear-gradient(to right, #28a745 0%, #218838 100%);
          color: white;
          border: none;
          border-radius: 50px;
          font-size: 18px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
        }

        .bi-btn-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4);
        }

        .bi-btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .bi-status {
          margin-top: 20px;
          padding: 15px;
          border-radius: 8px;
          text-align: center;
          font-weight: 600;
        }

        .bi-status.success {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .bi-status.error {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        /* Animation */
        .fade-in-up {
          animation: fadeInUp 0.5s ease-out forwards;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* --- MOBILE RESPONSIVE --- */
        @media (max-width: 768px) {
          .bi-section {
            padding-top: 100px;
            padding-bottom: 50px;
          }

          .bi-title {
            font-size: 28px;
          }

          .bi-intro {
            font-size: 16px;
            padding: 0 15px;
          }

          .bi-step-row {
            flex-direction: column !important;
            gap: 30px;
            text-align: left;
          }

          .bi-step-image {
            max-width: 100%; /* Full width on mobile */
          }

          .bi-form-card {
            padding: 30px 20px;
          }

          .bi-form-row {
            flex-direction: column;
            gap: 0;
          }

          .file-success {
            flex-direction: column;
            text-align: center;
            gap: 8px;
          }
          .file-info {
            text-align: center;
          }
        }
      `}</style>
    </section>
  );
};
